import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import type {
  WazuhAgentOverviewData,
  WazuhAlertsBySeverityData,
  WazuhClientItem,
  WazuhClientsListParams,
  WazuhClientsSummaryData,
  WazuhEndpointActionRequiredItem,
  WazuhMostVulnerableEndpointItem,
  WazuhOrganizationAttentionItem,
  WazuhPageParams,
  WazuhPagedData,
  WazuhPortfolioHealthMainAdminData,
  WazuhPortfolioHealthResellerData,
  WazuhPortfolioHealthSubOrgData,
  WazuhRecentIncidentItem,
  WazuhScopeParams,
  WazuhSecurityScoreData,
  WazuhThreatCategoriesData,
  WazuhTopCveItem,
  WazuhVulnerabilitiesBySeverityData,
  WazuhWidgetEnvelope,
} from 'src/types/mindtime/wazuhDashboard';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseDashboardWidget = <T>(raw: unknown, label: string): T => {
  if (!isRecord(raw)) {
    throw new Error(`Invalid response: missing ${label} envelope`);
  }

  if (typeof raw.success === 'boolean') {
    if (!raw.success) {
      throw new Error(
        typeof raw.message === 'string' ? raw.message : `${label} request was not successful`
      );
    }
    if (raw.data === undefined || raw.data === null) {
      throw new Error(`Invalid response: missing ${label} data`);
    }
    return raw.data as T;
  }

  const envelope = raw as WazuhWidgetEnvelope<T>;
  if (envelope.data === undefined || envelope.data === null) {
    throw new Error(`Invalid response: missing ${label} data`);
  }
  return envelope.data;
};

const parseDashboardPaged = <T>(raw: unknown, label: string): WazuhPagedData<T> => {
  const data = parseDashboardWidget<WazuhPagedData<T>>(raw, label);
  if (!Array.isArray(data.items)) {
    throw new Error(`Invalid response: missing ${label} items`);
  }
  return data;
};

const toNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const parseClientsSummary = (raw: unknown): WazuhClientsSummaryData => {
  const record = isRecord(raw) ? raw : {};
  const endpoints = isRecord(record.protectedEndpoints) ? record.protectedEndpoints : {};

  return {
    totalClients: toNumber(record.totalClients),
    protectedEndpoints: {
      total: toNumber(endpoints.total),
      warning: toNumber(endpoints.warning),
      critical: toNumber(endpoints.critical),
    },
    clientsRequiringAttention: toNumber(record.clientsRequiringAttention),
    clientsRequiringAttentionIds: Array.isArray(record.clientsRequiringAttentionIds)
      ? record.clientsRequiringAttentionIds.map(String)
      : [],
  };
};

const mapClientItem = (raw: unknown): WazuhClientItem => {
  const item = isRecord(raw) ? raw : {};
  const clientId = String(item.clientId ?? '').trim();

  return {
    clientId,
    clientName: toOptionalString(item.clientName) || '—',
    status: toOptionalString(item.status),
    risk: toOptionalString(item.risk),
    securityScore: typeof item.securityScore === 'number' ? item.securityScore : null,
    endpointCount: toNumber(item.endpointCount),
  };
};

export class WazuhDashboardAPI {
  constructor(private readonly getBaseUrl: () => string) {}

  private get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}${path}`, { params })
        .then((response) => {
          const parsedResponse = parse(response);
          resolve(parsedResponse?.data as T);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  getPortfolioHealthMainAdmin(
    params?: WazuhScopeParams
  ): Promise<WazuhPortfolioHealthMainAdminData> {
    return this.get<unknown>('/api/v1/dashboard/portfolio-health/main-admin', params).then((body) =>
      parseDashboardWidget<WazuhPortfolioHealthMainAdminData>(body, 'portfolio-health/main-admin')
    );
  }

  getPortfolioHealthReseller(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPortfolioHealthResellerData> {
    return this.get<unknown>('/api/v1/dashboard/portfolio-health/reseller', params).then((body) =>
      parseDashboardWidget<WazuhPortfolioHealthResellerData>(body, 'portfolio-health/reseller')
    );
  }

  getPortfolioHealthSubOrg(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPortfolioHealthSubOrgData> {
    return this.get<unknown>('/api/v1/dashboard/portfolio-health/sub-org', params).then((body) =>
      parseDashboardWidget<WazuhPortfolioHealthSubOrgData>(body, 'portfolio-health/sub-org')
    );
  }

  getAlertsBySeverity(params?: WazuhScopeParams): Promise<WazuhAlertsBySeverityData> {
    return this.get<unknown>('/api/v1/dashboard/alerts-by-severity', params).then((body) =>
      parseDashboardWidget<WazuhAlertsBySeverityData>(body, 'alerts-by-severity')
    );
  }

  getThreatCategories(params?: WazuhScopeParams): Promise<WazuhThreatCategoriesData> {
    return this.get<unknown>('/api/v1/dashboard/threat-categories', params).then((body) =>
      parseDashboardWidget<WazuhThreatCategoriesData>(body, 'threat-categories')
    );
  }

  getVulnerabilitiesBySeverity(
    params?: WazuhScopeParams
  ): Promise<WazuhVulnerabilitiesBySeverityData> {
    return this.get<unknown>('/api/v1/dashboard/vulnerabilities-by-severity', params).then((body) =>
      parseDashboardWidget<WazuhVulnerabilitiesBySeverityData>(body, 'vulnerabilities-by-severity')
    );
  }

  getTopCves(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPagedData<WazuhTopCveItem>> {
    return this.get<unknown>('/api/v1/dashboard/top-cves', params).then((body) =>
      parseDashboardPaged<WazuhTopCveItem>(body, 'top-cves')
    );
  }

  getOrganizationsAttention(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPagedData<WazuhOrganizationAttentionItem>> {
    return this.get<unknown>('/api/v1/dashboard/organizations/attention', params).then((body) =>
      parseDashboardPaged<WazuhOrganizationAttentionItem>(body, 'organizations/attention')
    );
  }

  getSecurityScore(params?: WazuhScopeParams): Promise<WazuhSecurityScoreData> {
    return this.get<unknown>('/api/v1/dashboard/security-score', params).then((body) =>
      parseDashboardWidget<WazuhSecurityScoreData>(body, 'security-score')
    );
  }

  getRecentIncidents(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPagedData<WazuhRecentIncidentItem>> {
    return this.get<unknown>('/api/v1/dashboard/recent-incidents', params).then((body) =>
      parseDashboardPaged<WazuhRecentIncidentItem>(body, 'recent-incidents')
    );
  }

  getAgentOverview(params?: WazuhScopeParams): Promise<WazuhAgentOverviewData> {
    return this.get<unknown>('/api/v1/dashboard/agent-overview', params).then((body) =>
      parseDashboardWidget<WazuhAgentOverviewData>(body, 'agent-overview')
    );
  }

  getEndpointsActionRequired(
    params?: WazuhScopeParams & WazuhPageParams
  ): Promise<WazuhPagedData<WazuhEndpointActionRequiredItem>> {
    return this.get<unknown>('/api/v1/dashboard/endpoints/action-required', params).then((body) =>
      parseDashboardPaged<WazuhEndpointActionRequiredItem>(body, 'endpoints/action-required')
    );
  }

  getMostVulnerableEndpoints(
    params?: WazuhScopeParams & WazuhPageParams & { resellerIds?: string }
  ): Promise<WazuhPagedData<WazuhMostVulnerableEndpointItem>> {
    return this.get<unknown>('/api/v1/dashboard/most-vulnerable-endpoints', params).then((body) =>
      parseDashboardPaged<WazuhMostVulnerableEndpointItem>(body, 'most-vulnerable-endpoints')
    );
  }

  getClientsSummary(params?: WazuhScopeParams): Promise<WazuhClientsSummaryData> {
    return this.get<unknown>('/api/v1/dashboard/clients/summary', params).then((body) =>
      parseClientsSummary(parseDashboardWidget<unknown>(body, 'clients/summary'))
    );
  }

  getClients(params?: WazuhClientsListParams): Promise<WazuhPagedData<WazuhClientItem>> {
    return this.get<unknown>('/api/v1/dashboard/clients', params).then((body) => {
      const data = parseDashboardPaged<unknown>(body, 'clients');
      return {
        ...data,
        items: (data.items ?? []).map(mapClientItem).filter((item) => Boolean(item.clientId)),
      };
    });
  }
}
